import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import FooterSection from './FooterSection';
import './ExamsPage.css';

export default function ExamsPage() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('All');
  const [selectedTerm, setSelectedTerm] = useState('All');

  // Fetch exam schedules from Express API
  useEffect(() => {
    const fetchExams = async () => {
      try {
        const response = await fetch('https://metapi.emedhatechnologies.com/api/exams/exams-schedule');
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          setExams(result.data);
        }
      } catch (error) {
        console.error('Error loading exam schedule:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, []);

  // Extract unique course dropdown options
  const courseOptions = [
    'All',
    ...Array.from(new Set(exams.map((e) => e.course_name).filter(Boolean)))
  ];

  // Extract unique term/semester dropdown options
  const termOptions = [
    'All',
    ...Array.from(new Set(exams.map((e) => e.term_label).filter(Boolean)))
  ];

  // Filtering Logic
  const filteredExams = exams.filter((exam) => {
    const query = searchTerm.toLowerCase();
    const matchesSearch =
      (exam.paper_title && exam.paper_title.toLowerCase().includes(query)) ||
      (exam.course_name && exam.course_name.toLowerCase().includes(query)) ||
      (exam.venue_mode && exam.venue_mode.toLowerCase().includes(query));

    const matchesCourse =
      selectedCourse === 'All' || exam.course_name === selectedCourse;

    const matchesTerm =
      selectedTerm === 'All' || exam.term_label === selectedTerm;

    return matchesSearch && matchesCourse && matchesTerm;
  });

  // Helper to format ISO date string to readable format
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      <Navbar />
      <div className="exams-page-wrapper">
        
        {/* 1. Luxurious Hero Banner */}
        <section className="exams-hero-banner">
          <div className="inner-banner-container">
            <nav className="breadcrumb-nav">
              <Link to="/" className="breadcrumb-link">Home</Link> / <span>Exams</span>
            </nav>
            <span className="hero-gold-badge">ACADEMIC EVALUATION &amp; SCHEDULES</span>
            <h1>Examination Timetable</h1>
            <p>View upcoming end-semester, annual, and backlog examination dates, times, and venues.</p>
          </div>
        </section>

        {/* 2. Control Bar: Search & Dropdown Filters */}
        <section className="exams-control-section">
          <div className="exams-container">
            
            <div className="filter-card-wrapper">
              
              {/* Search Box (Left Side) */}
              <div className="search-input-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search by paper title, course, or venue..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Dropdown Filters (Pushed to Far Right End) */}
              <div className="filters-right-group">
                
                {/* Course Filter Dropdown */}
                <div className="select-dropdown-box">
                  <label className="select-label">Filter Course:</label>
                  <select
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="filter-select"
                  >
                    {courseOptions.map((course, idx) => (
                      <option key={idx} value={course}>
                        {course}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Term/Semester Filter Dropdown */}
                <div className="select-dropdown-box">
                  <label className="select-label">Semester/Term:</label>
                  <select
                    value={selectedTerm}
                    onChange={(e) => setSelectedTerm(e.target.value)}
                    className="filter-select"
                  >
                    {termOptions.map((term, idx) => (
                      <option key={idx} value={term}>
                        {term}
                      </option>
                    ))}
                  </select>
                </div>

              </div>

            </div>

            {/* Results Count & Reset Bar */}
            <div className="results-count-bar">
              <span>{loading ? 'Loading exam schedules...' : `${filteredExams.length} examinations scheduled`}</span>
              {(selectedCourse !== 'All' || selectedTerm !== 'All' || searchTerm) && (
                <button
                  className="reset-filter-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCourse('All');
                    setSelectedTerm('All');
                  }}
                >
                  Reset Filters ✕
                </button>
              )}
            </div>

          </div>
        </section>

        {/* 3. Examination Schedule Grid / Table Section */}
        <section className="exams-grid-section">
          <div className="exams-container">
            
            {loading ? (
              <div className="no-exams-card">
                <h3>Loading Examination Timetable...</h3>
                <p>Please wait while we retrieve official university schedules.</p>
              </div>
            ) : filteredExams.length > 0 ? (
              <div className="exams-cards-grid">
                {filteredExams.map((exam) => (
                  <div key={exam.id} className="exam-item-card">
                    
                    {/* Top Accent Bar */}
                    <div className="card-top-accent"></div>

                    <div className="exam-card-body">
                      
                      {/* Course Header Badges */}
                      <div className="badge-header-row">
                        <span className="course-code-pill">
                          {exam.course_code ? `[${exam.course_code}]` : 'COURSE'} {exam.course_name}
                        </span>
                        <span className="term-badge">{exam.term_label || 'Semester'}</span>
                      </div>

                      {/* Paper Title */}
                      <h3 className="paper-title-heading">{exam.paper_title}</h3>

                      {/* Date, Time, and Venue Meta Box */}
                      <div className="exam-meta-details">
                        <div className="meta-row">
                          <span className="meta-label">📅 Exam Date:</span>
                          <span className="meta-value font-bold highlight-date">{formatDate(exam.exam_date)}</span>
                        </div>
                        <div className="meta-row">
                          <span className="meta-label">⏰ Time Slot:</span>
                          <span className="meta-value font-bold">{exam.exam_time || '10:00 - 13:00'}</span>
                        </div>
                        <div className="meta-row">
                          <span className="meta-label">🏛️ Venue / Mode:</span>
                          <span className="meta-value venue-tag">{exam.venue_mode || 'Authorized Center'}</span>
                        </div>
                      </div>

                      {/* Footer Notification Note */}
                      {/* <div className="card-action-footer">
                        <span className="hall-ticket-notice">
                          ℹ️ Admit Cards / Hall Tickets available on Student Portal
                        </span>
                      </div> */}

                    </div>

                  </div>
                ))}
              </div>
            ) : (
              <div className="no-exams-card">
                <h3>No exam schedules match your search criteria.</h3>
                <p>Try searching for a different course or reset your active filters.</p>
              </div>
            )}

          </div>
        </section>

      </div>
      <FooterSection />
    </>
  );
}